import React from 'react';
import ReactToolTip from 'react-tooltip';
import { generateId } from '../utils/genIds';

const FormInput = (props) => {
  const {
    helperText,
    label,
    description,
    errors = [],
    modelName,
    model,
    attribute,
    type,
    options = [],
    value,
    setValue,
    required,
    toolTip,
    placeholder,
    className = '',
  } = props;

  const inputMap = {
    textarea: TextArea,
    select: Select,
    checkbox: Checkbox,
    default: Input,
  };

  const id = generateId();
  const name = `${modelName}[${attribute}]`;

  let handleChange = null;
  if (typeof setValue === 'function') {
    handleChange = (e) => setValue(e.target.value);
  }

  let fundsTypeOptions = [];
  if (type === 'select') {
    fundsTypeOptions = Object.keys(options).map((e, i) => {
      return (
        <option key={i} value={options[e]}>
          {e.split('_').join(' ')}
        </option>
      );
    });
  }

  let componentProps = {
    required,
    id,
    name,
    defaultValue: value || model[attribute],
    onChange: handleChange,
    placeholder,
    hasError: errors.length > 0,
  };
  switch (type) {
    case 'select':
      componentProps = { ...componentProps, fundsTypeOptions };
      break;
    case 'textarea':
      break;
    default:
      componentProps = { ...componentProps, type };
  }

  const InputComponent = inputMap[type] || inputMap['default'];

  // Check if this is being used in signup form context
  const isSignupForm = className.includes('signup-input');

  return (
    <div className={className}>
      <label
        htmlFor={id}
        className={`block text-sm font-medium mb-1.5 ${
          isSignupForm ? 'text-slate-700' : 'text-gray-700'
        }`}
      >
        {label}
        {!!required && <span className="text-red-500 ml-0.5">*</span>}
        {!!toolTip && <ToolTip dataForId={id} dataTip={toolTip} />}
      </label>
      {!!description && (
        <p className="mb-2 text-sm text-slate-500">{description}</p>
      )}
      <div className={`relative ${type === "checkbox" ? "" : ""}`}>
        <InputComponent {...componentProps} isSignupForm={isSignupForm} />
        {errors.length > 0 && type !== 'checkbox' && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <svg
              className="h-5 w-5 text-red-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        )}
      </div>
      {errors.map((e, i) => {
        return (
          <p key={i} className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            {e}
          </p>
        );
      })}
      {helperText && <p className="mt-1.5 text-sm text-slate-500">{helperText}</p>}
    </div>
  );
};

const Select = (props) => {
  const {
    required,
    id,
    name,
    defaultValue,
    onChange,
    fundsTypeOptions,
    isSignupForm,
    hasError,
  } = props;

  return (
    <select
      required={required}
      id={id}
      name={name}
      defaultValue={defaultValue}
      onChange={onChange}
      className={`form-select block w-full rounded-lg transition-all duration-150 sm:text-sm ${
        isSignupForm
          ? `bg-slate-50 border-slate-200 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 ${hasError ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`
          : 'focus:ring-indigo-500 focus:border-indigo-500'
      }`}
    >
      {fundsTypeOptions}
    </select>
  );
};

const TextArea = (props) => {
  const { required, id, name, defaultValue, onChange, placeholder, isSignupForm, hasError } = props;

  return (
    <textarea
      required={required}
      id={id}
      name={name}
      defaultValue={defaultValue}
      onChange={onChange}
      placeholder={placeholder}
      className={`form-textarea block w-full rounded-lg transition-all duration-150 sm:text-sm ${
        isSignupForm
          ? `bg-slate-50 border-slate-200 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 ${hasError ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`
          : 'focus:ring-indigo-500 focus:border-indigo-500'
      }`}
    />
  );
};


const Checkbox = (props) => {
  const { required, id, name, defaultValue, isSignupForm } = props;
  return (
    <div className="flex items-center">
      <input
        type="hidden"
        name={name}
        value="false"
      />
      <input
        required={required}
        id={id}
        type="checkbox"
        name={name}
        defaultChecked={defaultValue}
        className={`form-checkbox h-5 w-5 rounded transition-all duration-150 ${
          isSignupForm
            ? 'text-amber-500 focus:ring-amber-500 border-slate-300'
            : 'text-indigo-600 focus:ring-indigo-500'
        }`}
      />
      <label
        htmlFor={id}
        className="ml-2 block text-sm leading-5 text-gray-900"
      ></label>
    </div>
  );
}

const Input = (props) => {
  const { required, id, type, name, defaultValue, onChange, placeholder, isSignupForm, hasError } = props;

  return (
    <input
      required={required}
      id={id}
      type={type}
      name={name}
      step={type == "number" ? "0.01" : null}
      defaultValue={defaultValue}
      onChange={onChange}
      placeholder={placeholder}
      className={`form-input block w-full rounded-lg transition-all duration-150 sm:text-sm disabled:bg-gray-100 disabled:opacity-50 ${
        isSignupForm
          ? `bg-slate-50 border-slate-200 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 placeholder:text-slate-400 ${hasError ? 'border-red-300 focus:ring-red-500 focus:border-red-500 pr-10' : ''}`
          : `focus:ring-indigo-500 focus:border-indigo-500 ${hasError ? 'border-red-300 pr-10' : ''}`
      }`}
    />
  );
};

export const ToolTip = (props) => {
  const { dataForId, dataTip } = props;

  return (
    <>
      <span data-for={dataForId} data-tip={dataTip} className="ml-1 text-slate-400 hover:text-slate-600 cursor-help">
        <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 inline-block">
          <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
            clipRule="evenodd"
          ></path>
        </svg>
        <ReactToolTip
          multiline
          id={dataForId}
          type="info"
          place="top"
          effect="solid"
        />
      </span>
    </>
  );
};

export default FormInput;
